import React, { forwardRef, useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { testImage, gradientBackground, getRole } from '@evertel/utils'
import { useService } from '@evertel/di'
import { DepartmentController } from '@evertel/department'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { SessionService } from '@evertel/session/SessionService'

interface DepartmentAvatarProps {
    departmentId: number
    className?: string
    width?: number
    height?: number
    style?: React.CSSProperties
    onClick?: (departmentId: number) => void
    /** Whose guest status to display. Defaults to the logged-in user. */
    userId?: number
    /** Whether to show the guest badge. Defaults to true. */
    showGuestBadge?: boolean
}

const DepartmentAvatar = observer(forwardRef<HTMLImageElement, DepartmentAvatarProps>(({
    departmentId,
    width = 40,
    height = 40,
    onClick,
    className,
    userId,
    showGuestBadge = true,
    ...otherProps
}, ref) => {

    const departmentController = useService(DepartmentController, [departmentId])
    const departmentsAccessController = useService(DepartmentsAccessController, [])
    const sessionService = useService(SessionService, [])

    const [isImageValid, setIsImageValid] = useState(true)

    useEffect(() => {
        departmentController.init(departmentId)
    }, [departmentId])

    useEffect(() => {
        const targetUserId = userId ?? sessionService.currentUserId
        if (targetUserId) {
            departmentsAccessController.init(targetUserId)
        }
    }, [userId, sessionService.currentUserId])

    useEffect(() => {
        // make sure image exists on server, if not, we render initials block

        if (departmentController.department?.publicImage) {
            testImage(departmentController.department.publicImage)
                .then(result => setIsImageValid(result !== 'error'))
                .catch(() => setIsImageValid(false))
        }
    }, [departmentController.department?.publicImage])

    // --- derived values ---

    const department = departmentController.department
    const publicImage = department?.publicImage
    const hasValidImage = !!(publicImage && isImageValid)

    const currentDepartmentAccess = departmentsAccessController.departmentAccess(departmentId)
    const isGuest = getRole(currentDepartmentAccess?.roles) === 'guest'

    // badge sizing — clamped so it's never too small or too big
    const badgeSize = Math.min(Math.max(width * 0.5, 14), 30)
    const negOffset = Math.max(width * -0.12, -6)
    const badgeFontSize = Math.max(badgeSize * 0.56, 10)

    const avatarClassNames = classNames(
        'profile-pic text-uppercase',
        {
            'cursor-pointer': onClick,
            'no-avatar': !hasValidImage,
            'profile-pic-sm': width <= 20 || height <= 20
        }
    )
    const handleClick = onClick ? () => onClick(departmentId) : undefined

    // --- render ---

    const renderAvatar = () => {
        if (hasValidImage) {
            return (
                <img
                    ref={ref}
                    src={publicImage}
                    className={avatarClassNames}
                    title={department?.name}
                    alt={department?.name}
                    width={width}
                    height={height}
                    onClick={handleClick}
                    {...otherProps}
                />
            )
        }

        const { style, ...rest } = otherProps
        const shortName = departmentController.shortName
        const divider = Math.max(shortName?.length - 3, 3)
        const fontSize = Math.max(10, Math.floor(width / divider))

        return (
            <div
                ref={ref}
                className={avatarClassNames}
                title={department?.name}
                style={{
                    ...style,
                    background: gradientBackground(department?.name),
                    // backgroundColor: stringToHslColor(departmentController.department?.name),
                    width,
                    height,
                    fontSize
                }}
                onClick={handleClick}
                {...rest}
            >
                {shortName}
            </div>
        )
    }

    return (
        <div
            style={{ position: 'relative', display: 'flex', height, width }}
            className={className}
        >
            {renderAvatar()}
            {isGuest && showGuestBadge && (
                <div
                    className="profile-pic-badge"
                    style={{
                        height: badgeSize,
                        width: badgeSize,
                        bottom: negOffset,
                        right: negOffset,
                        fontSize: badgeFontSize
                    }}
                >
                    G
                </div>
            )}
        </div>
    )
}))

export { DepartmentAvatar }
